import { useRef, useState } from "react";
import { api, type Progress } from "../lib/api";
import { addStructureFiles } from "../lib/structureImport";

// Admin-only controls. The server enforces this too -- these buttons merely
// stay out of a curator's way.

export function AdminBar(props: {
  onChanged: () => void;
  say: (m: string) => void;
  // The library export only carries what has been APPROVED. Without the count
  // on the button, an empty project hands out an empty file and says nothing.
  progress?: Progress;
}) {
  const file = useRef<HTMLInputElement>(null);
  const add = useRef<HTMLInputElement>(null);
  const addDir = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  // Adding builds to the OPEN project: files or a whole kit folder, ".schem"
  // as well as Minecraft's structure-block ".nbt". Nothing already in the
  // project is touched -- that is what "Projekt importieren" is for.
  const take = async (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (!files.length) return;
    setBusy(true);
    try {
      const res = await addStructureFiles(files, (done, total) =>
        props.say(`Lese ${done}/${total} Dateien…`),
      );
      const bad = res.failed;
      if (!res.added && !bad.length) {
        props.say("Keine .schem- oder .nbt-Dateien dabei.");
        return;
      }
      props.say(
        `${res.added} Bauten hinzugefügt` +
          (bad.length
            ? `, ${bad.length} übersprungen: ` +
              bad
                .slice(0, 3)
                .map((b) => `${b.name} (${b.reason})`)
                .join(", ") +
              (bad.length > 3 ? " …" : "")
            : ""),
      );
      if (res.added) props.onChanged();
    } catch (err) {
      props.say(String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="adminbar">
      <input
        ref={file}
        type="file"
        accept=".wgstudio,.wgstruct,application/json"
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f) return;
          setBusy(true);
          try {
            const res = await api.importProject(
              await f.text(),
              f.name.replace(/\.[^.]+$/, ""),
              true,
            );
            props.say(
              `${res.imported} Bauten importiert` +
                (res.skipped ? `, ${res.skipped} übersprungen` : ""),
            );
            props.onChanged();
          } catch (err) {
            props.say(String((err as Error).message));
          } finally {
            setBusy(false);
          }
        }}
      />
      <button disabled={busy} onClick={() => file.current?.click()}>
        Projekt importieren…
      </button>
      {/* One loader reads both formats (mc/schematic.cpp), so a build saved
          with a structure block needs no conversion step of its own. */}
      <input
        ref={add}
        type="file"
        accept=".schem,.nbt"
        multiple
        hidden
        onChange={async (e) => {
          const list = e.target.files;
          e.target.value = "";
          await take(list);
        }}
      />
      <button
        disabled={busy}
        title="Einzelne .schem- oder .nbt-Dateien an das offene Projekt anhängen — vorhandene Bauten und Urteile bleiben unangetastet."
        onClick={() => add.current?.click()}
      >
        Bauten hinzufügen…
      </button>
      <input
        ref={addDir}
        type="file"
        hidden
        // Not a React prop: the directory picker is a vendor attribute.
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
        onChange={async (e) => {
          const list = e.target.files;
          e.target.value = "";
          await take(list);
        }}
      />
      <button
        disabled={busy}
        title="Einen ganzen Kit-Ordner anhängen. Andere Dateien darin werden übergangen; die Unterordner werden als Ordner übernommen."
        onClick={() => addDir.current?.click()}
      >
        Ordner…
      </button>
      {/* ONE file. It holds every build with what was decided about it, and
          the generator takes the approved ones out of it -- so there is no
          second artefact to keep in step and no wrong one to hand over. */}
      {(() => {
        const p = props.progress;
        const n = p?.approved ?? 0;
        const all = p?.total ?? 0;
        return (
          <a
            className="linkish"
            href="/api/export/wgstudio"
            title={
              `Alle ${all} Bauwerke samt Urteil und Notizen. Der Generator ` +
              `nimmt sich daraus die ${n} übernommenen; das Aussortierte ` +
              `bleibt festgehalten, damit es niemand noch einmal durchsieht.`
            }
          >
            exportieren ({all})
            {n === 0 && <em> — noch nichts übernommen</em>}
          </a>
        );
      })()}
    </span>
  );
}
